import { IAttachment, IAttachmentData } from '../attachment';
import { IDepartmentData } from '../department';
import { IUser, IUserData } from '../user/user.types';
import {
  IWorkflowFormSectionField,
  IWorkflowFormSectionFieldData,
  IWorkflowFormTemplateRevision,
  IWorkflowFormTemplateRevisionData
} from '../workflow-form-template';
import { IWorkflowState, IWorkflowStateData } from '../workflow/workflow.types';

export interface IWorkflowFormSubmissionData {
  id?: number;
  form?: IWorkflowFormTemplateRevisionData | number;
  answers?: IWorkflowFormFieldSubmissionData[];
  assignee?: IUserData | null;
  state?: IWorkflowStateData;
  owner?: string;
  workflow?: number;
  number?: number;
  meta_fields?: Record<string, string>;
  created_at?: string | Date;
  updated_at?: string | Date;
  series_id?: string;
}

export interface IWorkflowFormFieldOptionSubmissionData {
  id?: number;
  option: number;
}

export interface IWorkflowFormSubmissionStateInfoData {
  display_name: string;
  color: string;
}

export interface IWorkflowFormFieldSubmissionData {
  id?: number;
  value: string;
  field: number | IWorkflowFormSectionFieldData;
  option_submissions: (IWorkflowFormFieldOptionSubmissionData | number)[];
  attachments?: IAttachmentData[];
  is_urgent?: boolean;
  template_index?: number;
}

export interface IWorkflowFormSectionSubmissionData {
  id?: number;
  fields: IWorkflowFormFieldSubmissionData[];
}

export interface IWorkflowFormSubmissionChecklistData {
  id?: number;
  assignee: IUserData | number;
  todo_template: IWorkflowFormSubmissionChecklistToDoTemplateData;
  form_submission: number;
  description: string;
  state?: number;
  completed_at: null | string;
  completed_by: null | string;
}

export interface IWorkflowFormSubmissionChecklistStateInfo {
  value: WorkflowFormSubmissionChecklistStateEnum;
  text?: string;
  icon?: string;
  color?: string;
}

export interface IWorkflowFormSubmissionChecklistToDoTemplateData {
  id?: number;
  todo_text: string;
  todo_department: number | IDepartmentData;
}

export interface IWorkflowFormSubmissionCommentData {
  id?: number;
  owner?: string;
  text: string;
  created_at?: string | Date;
  updated_at?: string | Date;

  mentions?: IUserData[];

  attachments?: IWorkflowFormSubmissionCommentAttachmentData[];
}

export interface IWorkflowFormSubmissionCommentAttachmentData {
  id?: number;
  file?: string | File;
  filename?: string;
}

export interface IWorkflowFormSubmissionCommentAttachment
  extends IWorkflowFormSubmissionCommentAttachmentData {
  file: string | File;
  filename: string;
}

export interface IWorkflowFormSubmission extends IWorkflowFormSubmissionData {
  assignee: IUser | null;
  form: IWorkflowFormTemplateRevision | number;
  answers: IWorkflowFormFieldSubmission[];
  state?: IWorkflowState;
  meta_fields: Record<string, string>;
  series_id: string;

  hasAnyUrgentAnswers(): boolean;
}

export interface IWorkflowFormFieldSubmission
  extends IWorkflowFormFieldSubmissionData {
  field: number | IWorkflowFormSectionField;
  option_submissions: (IWorkflowFormFieldOptionSubmission | number)[];
  attachments: IAttachment[];
  template_index: number;
  is_urgent: boolean;

  validate(): boolean;

  isRequired(answers: IWorkflowFormFieldSubmission[]): boolean;
  isAnswered(): boolean;

  getValue(): IAttachment[] | string;
}

export interface IWorkflowFormFieldOptionSubmission
  extends IWorkflowFormFieldOptionSubmissionData {
  validate(): boolean;
}

export interface IWorkflowFormSubmissionChecklist
  extends IWorkflowFormSubmissionChecklistData {
  validate(): boolean;
  getChecklistStateInfo(): IWorkflowFormSubmissionChecklistStateInfo;
}

export interface IWorkflowFormSubmissionChecklistToDoTemplate
  extends IWorkflowFormSubmissionChecklistToDoTemplateData {
  validate(): boolean;
}

export interface IWorkflowFormSubmissionComment
  extends IWorkflowFormSubmissionCommentData {
  readonly mentions: IUser[];
  readonly attachments: IWorkflowFormSubmissionCommentAttachment[];

  validate(): boolean;

  isCommentEdited: boolean;

  getTextHtml(): string;
}

export interface IWorkflowFormSectionSubmissionFieldErrorData {
  errors: string[];
}

export interface IWorkflowFormSubmissionCommentFieldErrorData {
  text?: string[];
}
export interface IWorkflowFormSubmissionFieldErrorData {
  form: string[];
  answers?: IWorkflowFormFieldSubmissionDataFieldErrorData[];
  owner?: string[];
}

export interface IWorkflowFormFieldSubmissionDataFieldErrorData {
  value: string[];
  field: string[];
  option_submissions: IWorkflowFormFieldOptionSubmissionFieldErrorData[];
}

export interface IWorkflowFormFieldOptionSubmissionFieldErrorData {
  option: string[];
  non_field_errors?: string[];
}

export interface IWorkflowFormSubmissionFieldError
  extends IWorkflowFormSubmissionFieldErrorData {
  clearErrors(): void;
}

export interface IWorkflowFormFieldSubmissionDataFieldError
  extends IWorkflowFormFieldSubmissionDataFieldErrorData {
  clearErrors(): void;
}

export interface IWorkflowFormFieldOptionSubmissionFieldError
  extends IWorkflowFormFieldOptionSubmissionFieldErrorData {
  clearErrors(): void;
}

export interface IWorkflowFormSubmissionCommentFieldError
  extends IWorkflowFormSubmissionCommentFieldErrorData {
  clearErrors(): void;
}

export enum WorkflowFormSubmissionChecklistStateEnum {
  InProgress,
  Completed,
  NotApplicable
}
